import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import { Feature } from 'ol';
import { click, platformModifierKeyOnly } from 'ol/events/condition';
import Polygon, { fromExtent } from 'ol/geom/Polygon';
import { DragBox } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { LineType } from '../models/enums/LineType';
import { FrequencySpectrum } from '../models/types/FrequencySpectrum';
import { FrequencySpectrumList } from '../models/types/FrequencySpectrumList';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { useMapStore } from '../stores/useMapStore';
import { useSeismicStore } from '../stores/useSeismicStore';
import { getAmplitude, getTrace, getXByTrace, getYByAmplitude } from '../utils/calculateScreenValuesUtils';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { GetFrequencySpectrumRequest, useGetFrequencySpectrum } from './attributes/api/useSeismicController';
import { FrequencySpectrumChart } from './FrequencySpectrumChart';
import { FrequencySpectrumChartLegend } from './FrequencySpectrumChartLegend';
import { FrequencySpectrumArea } from '../models/types/FrequencySpectrumArea';
import { getUrlParams } from 'features/seismic-3d/utils/Seismic3DUrlUtils';

export const FrequencySpectrumMenu = () => {
    const { map, seismicLayer } = useMapStore(state => ({
        map: state.map,
        seismicLayer: state.mainSeismicLayer
    }));

    const { scale, surveyMetadata, seismicCalculator, isInverted, navbarLoading, setNavbarLoading, traceRange, sortOption, traceRenderLimits } = useSeismicStore(state => ({
        scale: state.scale,
        surveyMetadata: state.surveyMetadata,
        seismicCalculator: state.calculator,
        isInverted: state.isInverted,
        navbarLoading: state.navbarLoading,
        setNavbarLoading: state.setNavbarLoading,
        traceRange: state.rangeTrace,
        sortOption: state.sortOption,
        traceRenderLimits: state.traceRenderLimits,
    }));
    const [ blockUpdates, setBlockUpdates ] = useState(false);
    const traceRangeRef = useRef(traceRange);

    const [ freqSpectrumLists, setFreqSpectrumList ] = useState<FrequencySpectrumList[]>([]);
    const { current: selectedExtentLayer } = useRef(new VectorLayer({ source: new VectorSource() }));
    const [ selectedFreqSpectrumAreasNumber, setSelectedFreqSpectrumAreasNumber ] = useState(0);
    const isLoading = useMemo(() => selectedFreqSpectrumAreasNumber > freqSpectrumLists.length, [freqSpectrumLists.length, selectedFreqSpectrumAreasNumber]);

    const { mutateAsync: getFrequencySpectrum } = useGetFrequencySpectrum();

    const { lineNumber, lineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        lineType: state.lineType,
    }));

    const [ minTrace, increment, sampleInterval, totalTraces, maxTrace ] = useMemo(() =>
        !!surveyMetadata && !!seismicCalculator ?
            [
                seismicCalculator.getMinTrace(surveyMetadata),
                seismicCalculator.getLineIncrement(surveyMetadata),
                seismicCalculator.calculateSampleInterval(surveyMetadata),
                seismicCalculator.getTotalTraces(surveyMetadata),
                seismicCalculator.getMaxTrace(surveyMetadata),
            ]
            :
            [
                1,
                1,
                1,
                1,
                1,
                1,
                1
            ]
    , [seismicCalculator, surveyMetadata]);

    const { current: sourceApi } = useRef(getParamsFromUrl().sourceApi);

    const baseParams = useMemo(() => {
        const layerParams = seismicLayer?.getParams();
        return !!layerParams ?
            ({
                volumeToken: layerParams.volumeToken!,
                filePath: layerParams.filePath!,
                byteSize: layerParams.byteSize!,
                volumeSamplesPerTrace: layerParams.volumeSamplesPerTrace!,
                lineIncrement: increment!,
                traceRangeStart: traceRange.initial,
                traceRangeEnd: traceRange.end,
                parallelProcesses: layerParams.parallelProcesses!,
                surveyInlineIncrement: layerParams.surveyInlineIncrement!,
                surveyXlineIncrement: layerParams.surveyXlineIncrement!,
                isIndexedVolume: layerParams.isIndexedVolume!,
                surveyInlineEnd: layerParams.surveyInlineEnd!,
                surveyInlineStart: layerParams.surveyInlineStart!,
                surveyXlineStart: layerParams.surveyXlineStart!,
                surveyXlineEnd: layerParams.surveyXlineEnd!,
                lineType: lineType!,
                sampleInterval: layerParams.sampleInterval!,
                skipTraceCount: layerParams.skipTraceCount!,
                surveyLineEnd: !!traceRenderLimits ? traceRenderLimits[0][1] : maxTrace!,
                surveyLineStart:  !!traceRenderLimits ? traceRenderLimits[0][0] : minTrace!,
                inlineDimensionName: layerParams.inlineDimensionName!,
                sampleDimensionName: layerParams.sampleDimensionName!,
                xlineDimensionName: layerParams.xlineDimensionName!,
                cdpDimensionName: layerParams.cdpDimensionName!,
                attributeCalculationMethod: layerParams.attributeCalculationMethod!,
                sourceApi: sourceApi,
                lineNumber: lineNumber,
                sortOption: sortOption,
                externalSSOAccessToken: getUrlParams().externalSSOAccessToken,
            })
            :
            null;
    }, [seismicLayer, increment, traceRange.initial, traceRange.end, lineType, traceRenderLimits, maxTrace, minTrace, sourceApi, lineNumber, sortOption]);

    const handleColorPicker = useCallback((freqSpectrumList: FrequencySpectrumList, color: string) => {
        setFreqSpectrumList(current => {
            const index = current.indexOf(freqSpectrumList);
            const newArray = [...current];
            newArray.splice(index, 1, { ...freqSpectrumList, color });
            return newArray;
        });
        const feature = selectedExtentLayer.getSource()?.getFeatureById(`${freqSpectrumList.startTrace}_${freqSpectrumList.endTrace}_${freqSpectrumList.startSample}_${freqSpectrumList.endSample}`);
        if (!!feature) {
            const style = feature.getStyle();
            if (style instanceof Style) {
                style.setStroke(new Stroke({ color: color, width: 5 }));
            }
            feature.changed();
        }
    }, [selectedExtentLayer]);

    const handleRemove = useCallback((freqSpectrumList: FrequencySpectrumList) => {
        setFreqSpectrumList(current => {
            const index = current.indexOf(freqSpectrumList);
            const newArray = [...current];
            newArray.splice(index, 1);
            return newArray;
        });
        const feature = selectedExtentLayer.getSource()?.getFeatureById(`${freqSpectrumList.startTrace}_${freqSpectrumList.endTrace}_${freqSpectrumList.startSample}_${freqSpectrumList.endSample}`);
        if (!!feature) {
            selectedExtentLayer.getSource()?.removeFeature(feature);
        }
        setSelectedFreqSpectrumAreasNumber(current => current - 1);
    }, [selectedExtentLayer]);

    const handleUpdateLimits = useCallback((freqSpectrumList: FrequencySpectrumList, limits: FrequencySpectrumArea) => {
        const feature = selectedExtentLayer.getSource()?.getFeatureById(`${freqSpectrumList.startTrace}_${freqSpectrumList.endTrace}_${freqSpectrumList.startSample}_${freqSpectrumList.endSample}`);
        if (!!feature && !!baseParams) {

            const maxTraceLimit = surveyMetadata?.IsIndexed ?
                !!traceRenderLimits?
                    traceRenderLimits[0][1]
                    :
                    maxTrace
                :
                traceRange.end;
            const minTraceLimit = surveyMetadata?.IsIndexed ?
                !!traceRenderLimits ?
                    traceRenderLimits[0][0]
                    :
                    minTrace
                :
                traceRange.initial;

            let [ startTrace, endTrace ] = [ limits.startTrace, limits.endTrace ];

            if (startTrace > maxTraceLimit || endTrace < minTraceLimit) {
                return;
            }

            if (startTrace < minTraceLimit) {
                startTrace = minTraceLimit;
            }
            if (endTrace > maxTraceLimit) {
                endTrace = maxTraceLimit;
            }

            let [ minY, maxY ] = [ getYByAmplitude(limits.startTime, scale.y, sampleInterval), getYByAmplitude(limits.endTime, scale.y, sampleInterval)];
            [ minY, maxY ] = [ Math.round(minY), Math.round(maxY) ];

            if (minY === maxY) {
                maxY--;
            }
            console.log([getAmplitude(minY, scale.y, sampleInterval), getAmplitude(maxY, scale.y, sampleInterval)]);

            const [ startSample, endSample ] = [ minY, maxY ];
            if (minY > maxY) {
                [ minY, maxY ] = [ maxY, minY ];
            }

            let [ minX, maxX ] = [ getXByTrace((startTrace - minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces), getXByTrace((endTrace - minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces) ];
            if (minX > maxX) {
                [ minX, maxX ] = [ maxX, minX ];
            }

            const newExtent = [
                minX,
                minY,
                maxX,
                maxY,
            ];
            feature.setGeometry(fromExtent(newExtent));
            feature.setId(`${startTrace}_${endTrace}_${startSample}_${endSample}`);
            feature.changed();
            const index = freqSpectrumLists.indexOf(freqSpectrumList);
            setFreqSpectrumList(current => {
                const newArray = [...current];
                newArray.splice(index, 1);
                return newArray;
            });
            getFrequencySpectrum({
                ...baseParams,
                lineStart: startTrace,
                lineEnd: endTrace,
                lineStartSample: Math.abs(startSample),
                lineEndSample: Math.abs(endSample),
            }).then(freqSpectrums => {
                setFreqSpectrumList(current => {
                    const newArray = [...current];
                    newArray.splice(index, 0, {startTrace, endTrace, startSample, endSample, frequencySpectrums: freqSpectrums, color: freqSpectrumList.color });
                    return newArray;
                });
            });
        }
    }, [baseParams, freqSpectrumLists, getFrequencySpectrum, increment, isInverted, maxTrace, minTrace, sampleInterval, scale.x, scale.y, selectedExtentLayer, surveyMetadata?.IsIndexed, totalTraces, traceRange.end, traceRange.initial, traceRenderLimits]);

    const dragBoxCallback = useCallback((dragBox: DragBox) => {
        if (!map || !seismicLayer || !baseParams) {
            return;
        };

        const selectedExtent = dragBox.getGeometry().getExtent();
        let startTrace = getTrace(selectedExtent[0], scale.x, minTrace, increment);
        let endTrace =  getTrace(selectedExtent[2], scale.x, minTrace, increment);

        if (isInverted) {
            [ startTrace, endTrace ] = [ maxTrace - endTrace + minTrace, maxTrace - startTrace + minTrace ];
        }

        let startSample = Math.round(parseInt(getAmplitude(selectedExtent[1], scale.y, sampleInterval)) / sampleInterval);
        let endSample = Math.round(parseInt(getAmplitude(selectedExtent[3], scale.y, sampleInterval)) / sampleInterval);

        if (startSample > endSample) {
            [ startSample, endSample ] = [ endSample, startSample ];
        }
        if (startTrace > endTrace) {
            [ startTrace, endTrace ] = [ endTrace, startTrace ];
        }

        if (startSample === endSample) {
            endSample +=1;
        }

        const maxTraceLimit = surveyMetadata?.IsIndexed ?
            !!traceRenderLimits?
                traceRenderLimits[0][1]
                :
                maxTrace
            :
            traceRange.end;
        const minTraceLimit = surveyMetadata?.IsIndexed ?
            !!traceRenderLimits ?
                traceRenderLimits[0][0]
                :
                minTrace
            :
            traceRange.initial;

        if (startTrace > maxTraceLimit || endTrace < minTraceLimit) {
            return;
        }

        if (startTrace < minTraceLimit) {
            startTrace = minTraceLimit;
            selectedExtent[0] = getXByTrace((minTraceLimit-minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces);
        }
        if (endTrace > maxTraceLimit) {
            endTrace = maxTraceLimit;
            selectedExtent[2] = getXByTrace((maxTraceLimit-minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces);
        }

        const params = ({
            ...baseParams,
            lineStartSample: Math.min(startSample, endSample),
            lineEndSample: Math.max(startSample, endSample),
            lineStart: Math.min(startTrace, endTrace),
            lineEnd: Math.max(startTrace, endTrace),
        });
        const color = '#'+(Math.random() * 0xFFFFFF << 0).toString(16).padStart(6, '0');
        const feature = new Feature({
            geometry: fromExtent(selectedExtent)
        });
        feature.setId(`${startTrace}_${endTrace}_${startSample}_${endSample}`);
        feature.setStyle(new Style({
            stroke: new Stroke({
                color: color,
                width: 5,
            }),
            fill: new Fill({ color: 'rgba(0, 0, 0, 0)'})
        }));
        selectedExtentLayer.getSource()?.addFeature(feature);
        setSelectedFreqSpectrumAreasNumber(current => current + 1);
        getFrequencySpectrum(params).then((freqSpectrum) => {
            setFreqSpectrumList(current => [...current, { startSample, endSample, startTrace, endTrace, frequencySpectrums: freqSpectrum, color }]);
        });

        if (!map.getLayers().getArray().includes(selectedExtentLayer)) {
            map.addLayer(selectedExtentLayer);
        }
    }, [baseParams, getFrequencySpectrum, increment, isInverted, map, maxTrace, minTrace, sampleInterval, scale.x, scale.y, seismicLayer, selectedExtentLayer, surveyMetadata?.IsIndexed, totalTraces, traceRange.end, traceRange.initial, traceRenderLimits]);

    const dragBoxInteraction = useMemo(() => {
        const interaction = new DragBox({
            condition: platformModifierKeyOnly
        });
        interaction.on('boxend', () => dragBoxCallback(interaction));
        return interaction;
    }, [dragBoxCallback]);

    const dragBoxInteractionRef = useRef(dragBoxInteraction);

    useEffect(() => {
        if (!!map && !!seismicLayer && !!baseParams) {
            map.removeInteraction(dragBoxInteractionRef.current);
            map.addInteraction(dragBoxInteraction);
            dragBoxInteractionRef.current = dragBoxInteraction;
        }
    }, [baseParams, dragBoxInteraction, map, seismicLayer]);

    //Cleanup
    useEffect(() => {
        return () => {
            map?.removeInteraction(dragBoxInteractionRef.current);
            map?.removeLayer(selectedExtentLayer);
        };
    }, []);

    useEffect(() => {
        setFreqSpectrumList([]);
        setSelectedFreqSpectrumAreasNumber(0);
        selectedExtentLayer.getSource()?.clear();
    }, [lineType]);

    useEffect(() => {
        selectedExtentLayer.getSource()?.clear();
        freqSpectrumLists.forEach(freqSpectrumList => {
            const color = freqSpectrumList.color;
            let { startTrace, endTrace, startSample, endSample } = freqSpectrumList;

            let [ minX, minY, maxX, maxY ] = [
                getXByTrace((startTrace - minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces),
                getYByAmplitude(endSample * sampleInterval, scale.y, sampleInterval),
                getXByTrace((endTrace - minTrace)/increment, scale.x, minTrace, 1, isInverted, totalTraces),
                getYByAmplitude(startSample * sampleInterval, scale.y, sampleInterval),
            ];

            if (isInverted) {
                [ minX, maxX ] = [ maxX, minX];
            }

            const feature = new Feature({
                geometry: fromExtent([minX, minY, maxX, maxY])
            });
            feature.setId(`${startTrace}_${endTrace}_${startSample}_${endSample}`);
            feature.setStyle(new Style({
                stroke: new Stroke({
                    color: color,
                    width: 5,
                }),
                fill: new Fill({ color: 'rgba(0, 0, 0, 0)'})
            }));
            selectedExtentLayer.getSource()?.addFeature(feature);
        });
    }, [scale.x, scale.y, isInverted]);

    useEffect(() => {
        if (freqSpectrumLists.length > 0 && !!baseParams) {
            const currentFreqSpectrums = [...freqSpectrumLists];
            setFreqSpectrumList([]);
            const promises : Promise<FrequencySpectrum[]>[] = [];
            currentFreqSpectrums.forEach(currentFreqSpectrumList => {
                const promise = getFrequencySpectrum({
                    ...baseParams,
                    lineStart: currentFreqSpectrumList.startTrace,
                    lineEnd: currentFreqSpectrumList.endTrace,
                    lineStartSample: currentFreqSpectrumList.startSample,
                    lineEndSample: currentFreqSpectrumList.endSample,
                    lineNumber,
                    sortOption
                });
                promises.push(promise);
            });

            Promise.allSettled(promises).then((results) => {
                const newFreqSpectrumLists: FrequencySpectrumList[] = [];
                results.forEach((result, i) => {
                    if (result.status === 'fulfilled') {
                        const frequencySpectrums = result.value;
                        newFreqSpectrumLists.push({
                            ...currentFreqSpectrums[i],
                            frequencySpectrums,
                        });
                    }
                });

                setFreqSpectrumList(current => {
                    const newArray = [...current];
                    newArray.unshift(...newFreqSpectrumLists);
                    return newArray;
                });
            });
        }
    }, [lineNumber, sortOption]);

    useEffect(() => setNavbarLoading(isLoading), [isLoading]);

    return (
        <Stack gap={1} width='100%' padding='20px' sx={{overflow: 'auto'}}>
            <Stack gap={2} sx={(theme) => ({ [theme.breakpoints.down('lg')]: { flexDirection: 'column-reverse' }, [theme.breakpoints.up('lg')]: { direction: 'column' },  })}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>Instructions</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack gap={1}>
                            <Typography>1. Hold Ctrl, then click and drag to select a line segment and view its frequency spectrum.</Typography>
                            <Typography>2. You can select multiple areas and plot their frequency spectrums together.</Typography>
                            <Typography>2. Click and drag in a chart area to zoom the chart into it.</Typography>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
                <FrequencySpectrumChart isLoading={isLoading} freqSpectrumLists={freqSpectrumLists}/>
            </Stack>
            <Stack direction='row' justifyContent='space-between' sx={(theme) => ({ marginTop:'-50px', [theme.breakpoints.down('xl')]: { marginTop: '0px' }})}>
                <Stack>
                    {
                        freqSpectrumLists.map((freqSpectrumList, i) =>
                            <FrequencySpectrumChartLegend
                                key={i} onChangeColor={(color) => handleColorPicker(freqSpectrumList, color)}
                                freqSpectrumList={freqSpectrumList}
                                onRemove={() => handleRemove(freqSpectrumList)}
                                onUpdateLimits={(limits) => handleUpdateLimits(freqSpectrumList, limits)}
                            />
                        )
                    }
                </Stack>
            </Stack>
        </Stack>
    );
};